import { GuruJuaraBadge } from "components/kelas-live/assets/guru-juara-badge";
import {
  getClassTimeStamp,
  getMediaURL,
  getTimeStampFromDateString
} from "components/kelas-live/utils";
import { SubjectTag } from "components/subject-tag/subject-tag";
import { SubjectTagData } from "components/subject-tag/subject-tag.data";
import { format, intervalToDuration } from "date-fns";
import Image from "next/legacy/image";
import React, { useState } from "react";
import { ClassSession } from "services/api/sessions/sessionService.types";
import {
  ClassCard,
  ClassCardContainer,
  ClassCardDetails,
  ClassCardDetailsContent,
  ClassCardDetailsHeading,
  ClassCardDetailsTeacher,
  ClassCardDetailsTeacherContainer,
  ClassCardImageContainer,
  ClassCardImageWrapper,
  ClassCardInfoItem,
  ClassCardInfoItemContainer,
  ClassCardTopicsContainer,
  ClassTagContainer,
  ClassCardTimer,
  ClassCardTimerDots,
  ClassCardTimerHighlight,
  StyledButton,
  ClassCardTitle
} from "./upcoming-class-hero-section-card.styles";
import { ClassTags } from "components/kelas-live/class-tags/class-tags";
import { StatusDot } from "components/kelas-live/assets/status-dot/status-dot";
import { ClassNotStartedYetModal } from "../../class-not-started-yet-modal/class-not-started-yet-modal";
import trackEvent from "services/mixpanel/mixpanel";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";
import { theme } from "styles/theme";

interface Props {
  handleClick: () => void;
  classSession: ClassSession;
  baseMediaURL: string;
  currentTimeStamp: string;
  tag?: { title: string; cardType: string };
  cardPosition: number;
}

export const UpcomingClassHeroSectionCard: React.FC<Props> = ({
  handleClick,
  classSession,
  baseMediaURL,
  tag,
  currentTimeStamp,
  cardPosition
}) => {
  const [showClassNotStartedModal, setShowClassNotStartedModal] =
    useState(false);

  const subjectTagData = SubjectTagData[classSession.subject.toUpperCase()];
  const classSessionUpcomingTime = intervalToDuration({
    start: getTimeStampFromDateString(currentTimeStamp),
    end: getTimeStampFromDateString(classSession.startDate)
  });

  const sendUpcomingCardClickEvent = () => {
    trackEvent({
      eventName: kelasLiveEvents.upcomingClassDetailClicked,
      payload: {
        liveHomePageDesignVersion:
          "Version 4 - Disabled Join CTA on Pinned Class Card",
        clickedFrom: "Pinned Card of concept and extra class",
        cardPosition: cardPosition || null
      }
    });
  };

  const sendJoinButtonClickedEvent = () => {
    trackEvent({
      eventName: kelasLiveEvents.liveClassJoinButtonPressed,
      payload: {
        buttonStatus: "Disabled",
        source: "Pinned Class Card"
      }
    });
  };

  const handleCTAClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    sendUpcomingCardClickEvent();
    sendJoinButtonClickedEvent();
    setShowClassNotStartedModal(true);
  };

  const handleCardClick = () => {
    sendUpcomingCardClickEvent();
    handleClick();
  };

  const getBackgroundColor = () => {
    if (tag?.cardType === "FIRST_MANDATORY") {
      return theme.colors.yellow["200"];
    } else if (tag?.cardType === "NEXT_MANDATORY") {
      return theme.colors.blue["100"];
    } else {
      return theme.colors.green["100"];
    }
  };

  return (
    <>
      <ClassCardContainer>
        {tag && (
          <ClassTagContainer backgroundColor={getBackgroundColor()}>
            <StatusDot classType={tag?.cardType} />
            <ClassCardTitle>{tag.title}</ClassCardTitle>
            <StatusDot classType={tag?.cardType} />
          </ClassTagContainer>
        )}
        <ClassCard onClick={handleCardClick}>
          <ClassCardImageContainer>
            <ClassCardImageWrapper>
              <Image
                src={getMediaURL(baseMediaURL, classSession.teacher.avatarUrl)}
                alt={classSession.teacher.name}
                layout="fill"
                objectFit="fill"
              />
            </ClassCardImageWrapper>
          </ClassCardImageContainer>

          <ClassCardDetails>
            <ClassCardTopicsContainer>
              <li>
                <SubjectTag
                  backgroundColor={subjectTagData?.backgroundColor}
                  color={subjectTagData?.color}
                  text={subjectTagData?.text}
                  iconSrc={subjectTagData?.iconSrc}
                />
              </li>
              <ClassTags tags={classSession.tags} />
            </ClassCardTopicsContainer>
            <ClassCardDetailsHeading>
              {classSession.topic}
            </ClassCardDetailsHeading>
            <ClassCardDetailsContent>
              <ClassCardDetailsTeacherContainer>
                <ClassCardDetailsTeacher>
                  {classSession.teacher.name} <GuruJuaraBadge />
                </ClassCardDetailsTeacher>
                <ClassCardInfoItemContainer>
                  <ClassCardInfoItem>
                    {getClassTimeStamp(classSession.startDate)}
                  </ClassCardInfoItem>
                  <ClassCardInfoItem>
                    {format(
                      getTimeStampFromDateString(classSession.startDate),
                      "k:mm"
                    )}
                  </ClassCardInfoItem>
                  <ClassCardInfoItem>
                    {classSession.duration} menit
                  </ClassCardInfoItem>
                </ClassCardInfoItemContainer>
              </ClassCardDetailsTeacherContainer>
              <StyledButton variant="primary" onClick={handleCTAClick}>
                Masuk sekarang
              </StyledButton>
            </ClassCardDetailsContent>
            <ClassCardTimer>
              <span>Mulai dalam </span>
              <ClassCardTimerHighlight>
                {classSessionUpcomingTime.days} hari
              </ClassCardTimerHighlight>
              <ClassCardTimerDots>:</ClassCardTimerDots>
              <ClassCardTimerHighlight>
                {classSessionUpcomingTime.hours} jam
              </ClassCardTimerHighlight>
              <ClassCardTimerDots>: </ClassCardTimerDots>
              <ClassCardTimerHighlight>
                {classSessionUpcomingTime.minutes} menit
              </ClassCardTimerHighlight>
            </ClassCardTimer>
          </ClassCardDetails>
        </ClassCard>
      </ClassCardContainer>
      {showClassNotStartedModal && (
        <ClassNotStartedYetModal
          classSession={classSession}
          currentTimeStamp={currentTimeStamp}
          onClose={() => setShowClassNotStartedModal(false)}
        />
      )}
    </>
  );
};
